import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { C } from '@parsina/framework/clinical/src/constants/theme';
import { soft } from '@parsina/framework/tokens';
import { Button, useKeyboardInset } from '@parsina/framework';
import { ReceptionMobileListSkeleton } from './ReceptionSkeleton';
import { Building2, CalendarDays, PhoneCall, Plus } from 'lucide-react';
import ConfirmDialog from '@parsina/framework/clinical/src/components/common/ConfirmDialog';
import ErrorState from '@parsina/framework/clinical/src/components/common/ErrorState';
import MobileHeader from '@parsina/framework/clinical/src/components/mobile/MobileHeader';
import ReceptionContextCard from '../../components/mobile/ReceptionContextCard';
import DayStatStrip from '../../components/mobile/DayStatStrip';
import WeekPickerSheet from '../../components/mobile/WeekPickerSheet';
import MobileCreditCard from '../../components/mobile/MobileCreditCard';
import MobileNextPatientCard from '../../components/mobile/MobileNextPatientCard';
import MobileAppointmentCard from '../../components/mobile/MobileAppointmentCard';
import MobileForgottenVisitAlert from '../../components/mobile/MobileForgottenVisitAlert';
import NoAppointments from './NoAppointments';
import BookAppointmentDialog from '../../components/BookAppointmentDialog';
import RescheduleAppointmentDialog from '../../components/RescheduleAppointmentDialog';
import { useReceptionBooking } from '../../hooks/useReceptionBooking';
import { useOptionalToast } from '@parsina/framework/clinical/src/lib/useOptionalToast';
import NoClinicGuide from '../../components/NoClinicGuide.jsx';
import { useDoctor } from '@parsina/framework/clinical/src/hooks/useDoctor';
import { ALL_CLINICS, useActiveClinic, scopeToClinic } from '../../hooks/useActiveClinic';
import { useWalletBalance } from '@parsina/framework/clinical/src/hooks/useWalletBalance';
import { useReception, forgottenVisits } from '../../hooks/useReception';
import { usePresenceSchedule } from '../../hooks/usePresenceSchedule';
import { buildDayModel, futureListFor, dayCounts, makeDateLabel, shiftISO, resolvePlace } from './Reception.shared';
import { useTranslation } from '@parsina/framework/clinical/src/i18n/index';
import { IRAN_OFFSET_MS } from '@parsina/framework/clinical/src/lib/jalali';

// The mobile پذیرش desk — a LIST-DOMINANT cockpit (docs/DOCTOR_MOBILE_RECEPTION_
// REDESIGN.md): the appointments are the screen's purpose, so the pinned chrome
// is held to ~28% of a phone's usable height (it was ~61%).
//
//   ① gradient header: no greeting — one context card holding «کجا هستم»
//      (where the presence schedule puts the doctor) over the day bar
//   ② one 44px strip: the day's counts (income lives in حسابداری, a tab)
//   ③ alerts → ④ hero → ⑤ the day's card list, «فراخوان بیمار بعدی» docked
//
// Week browsing lives in a sheet opened from the date label. Patient lookup is
// not a reception action any more (/lookup stays routable). Rendered from the
// mobile component library over the same day-model the web surface uses.


// Client-side list window: the bounded day list grows through an explicit
// «نمایش بیشتر» expander — never infinite scroll (mobile concept §4).
const PAGE_SIZE = 10;

const ReceptionMobile = ({ navigate }) => {
  const keyboardInset = useKeyboardInset();
  const { t, formatDate, formatNumber } = useTranslation();
  const { doctor } = useDoctor();
  const walletBalance = useWalletBalance(doctor?.id);

  const [tick, setTick] = React.useState(Date.now());

  // 30s: the wait/visit clocks are minute-grained; a stale minute reads as a bug.
  React.useEffect(() => {
    const id = setInterval(() => setTick(Date.now()), 30_000);
    return () => clearInterval(id);
  }, []);

  const now    = new Date(tick + IRAN_OFFSET_MS);
  const nowMin = now.getUTCHours() * 60 + now.getUTCMinutes();
  const todayISO = now.toISOString().split('T')[0];

  // Selected day — an absolute ISO date (Iran-local), driven by the arrows AND
  // the calendar sheet, same as the desktop header's picker. Same URL contract
  // as the web surface: ?date=YYYY-MM-DD, today is the default and stays out of
  // the URL, `replace` keeps day-stepping from stacking history entries.
  const [searchParams, setSearchParams] = useSearchParams();
  const rawDate = searchParams.get('date');
  const selISO  = /^\d{4}-\d{2}-\d{2}$/.test(rawDate || '') ? rawDate : todayISO;
  const setSelISO = (v) => {
    const next = new URLSearchParams(searchParams);
    if (!v || v === todayISO) next.delete('date'); else next.set('date', v);
    setSearchParams(next, { replace: true });
  };
  const isToday   = selISO === todayISO;
  // Noon avoids DST/rounding edge cases (same trick as the web surface).
  const selIranMs = Date.parse(`${selISO}T12:00:00Z`);

  const { appointments: allAppointments, openVisits, loading, error, retry, endVisit, withdrawRequest, reload } = useReception(selISO);
  const toast = useOptionalToast();
  const booking = useReceptionBooking({ selISO, reload, withdrawRequest, toast });

  // Where the schedule puts the doctor on the selected day. The lens shows every
  // clinic unless the doctor picked one — or «خودکار», which follows this — today:
  // the same hook as the web surface, so the two cannot claim different clinics.
  const presence = usePresenceSchedule();
  const placeFor = React.useCallback(
    (clinics) => resolvePlace({ ...presence, clinics, dayISO: selISO, isToday, nowMin }),
    [presence, selISO, isToday, nowMin],
  );
  const scheduledFor = React.useCallback((clinics) => placeFor(clinics).scheduledClinicId, [placeFor]);
  const clinic = useActiveClinic({ scheduledFor, todayISO });
  const place = React.useMemo(
    () => (clinic.loading ? { state: 'unknown' } : placeFor(clinic.clinics)),
    [clinic.loading, clinic.clinics, placeFor],
  );
  const [weekOpen, setWeekOpen] = React.useState(false);
  const { rows: appointments, hidden: hiddenByClinic } = React.useMemo(
    () => scopeToClinic(allAppointments, clinic.filterId),
    [allAppointments, clinic.filterId],
  );
  const clinicNameOf = React.useCallback(
    (apt) => (clinic.isAll && clinic.clinics.length > 1
      ? (clinic.clinics.find(c => c.id === apt.clinicId)?.name || '')
      : ''),
    [clinic.isAll, clinic.clinics],
  );
  const clinicCounts = React.useMemo(() => {
    const m = new Map();
    (allAppointments || []).forEach(a => {
      if (a.clinicId) m.set(a.clinicId, (m.get(a.clinicId) || 0) + 1);
    });
    return m;
  }, [allAppointments]);
  const dateLabel  = makeDateLabel(selIranMs, { formatDate });

  // One shared day-model with the web surface — same queue order, same next patient.
  const model = React.useMemo(
    () => buildDayModel({ appointments, isToday, nowMin }),
    [appointments, isToday, nowMin],
  );
  const futureList = React.useMemo(
    () => futureListFor(model, { isToday, nowMin }),
    [model, isToday, nowMin],
  );
  const counts = dayCounts(model.dayApts);

  const nextApt  = isToday ? model.nextApt : null;
  const listApts = nextApt ? futureList.filter(a => a.id !== nextApt.id) : futureList;

  const [visibleCount, setVisibleCount] = React.useState(PAGE_SIZE);
  React.useEffect(() => { setVisibleCount(PAGE_SIZE); }, [selISO, listApts.length]);
  const visibleAppointments = listApts.slice(0, visibleCount);
  const hiddenCount = Math.max(0, listApts.length - visibleCount);

  // Visits presumed forgotten (open > 2h, or from a previous day) — never the one
  // the hero is already showing.
  const forgotten = forgottenVisits(openVisits, tick)
    .filter(a => !nextApt || a.id !== nextApt.id);

  // Closing a visit is not undoable — route every «پایان» through a confirm.
  const [pendingEnd, setPendingEnd] = React.useState(null);
  const askEnd = (apt) => setPendingEnd(apt);

  const showCta = isToday && nextApt && !error;
  const listState = loading ? 'loading' : error ? 'error' : futureList.length === 0 ? 'empty' : 'content';

  return (
    <div style={{ position: 'absolute', inset: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden', background: C.bg }}>

      {/* ── Pinned cockpit chrome: header (place + day) and the stat strip ──── */}
      <div style={{ flexShrink: 0 }}>
        {/* No greeting row: the doctor's name and پارسه balance live in the
            bottom bar's «بیشتر» sheet, and the header's whole height goes to
            what this screen is about — where, and which day. */}
        <MobileHeader>
          {/* Visually hidden at its natural width (clip-path, not a 1px box):
              the screen keeps its one <h1> for assistive tech. */}
          <h1 data-visually-hidden style={{ position: 'absolute', clipPath: 'inset(50%)', whiteSpace: 'nowrap', margin: 0, pointerEvents: 'none' }}>
            {t('dashboard.title')}
          </h1>
          {/* Place and day answer the same question — "what am I looking at" —
              so they share one card on the brand chrome. No clinic at all: no
              place line; the list shows the connection guide. */}
          <ReceptionContextCard
            place={clinic.none ? { state: 'no_clinic' } : place}
            clinic={clinic}
            counts={clinicCounts}
            isToday={isToday}
            selMs={selIranMs}
            dateLabel={dateLabel}
            onPrev={() => setSelISO(shiftISO(selISO, -1))}
            onNext={() => setSelISO(shiftISO(selISO, 1))}
            onToday={() => setSelISO(todayISO)}
            onOpenWeek={() => setWeekOpen(true)}
          />
        </MobileHeader>

        <DayStatStrip counts={counts} loading={loading} isToday={isToday} />
      </div>

      {/* ── Scrollable list ─────────────────────────────────────────────────── */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', WebkitOverflowScrolling: 'touch', paddingBottom: showCta ? '5.5rem' : '1rem' }}>

        {/* Error replaces the list region; the header + date nav stay operable. */}
        {error ? (
          <div data-testid="reception-content" data-state="error">
            <ErrorState error={error} title={t('errors.load.data')} onRetry={retry} />
          </div>
        ) : (
          <div data-testid="reception-content" data-state={listState}>

            {/* ③ Alerts, most urgent first: a forgotten open visit, rows the
                clinic lens is holding back, low credit. */}
            <div data-testid="reception-alerts">
              <MobileForgottenVisitAlert
                visits={forgotten}
                onContinue={apt => navigate('visit', { id: apt.patientId })}
                onEnd={askEnd}
              />
              {/* A lens that hides appointments says so and offers one tap out
                  — the safety net that lets the lens follow the schedule. */}
              {hiddenByClinic > 0 && (
                <div style={{ padding: '0.75rem 0.875rem 0' }}>
                  <Button
                    variant="subtle"
                    data-testid="reception-clinic-hidden"
                    className="w-full justify-start gap-2 min-h-11"
                    onClick={() => clinic.setActive(ALL_CLINICS)}
                  >
                    <Building2 size={15} aria-hidden />
                    <span style={{ flex: 1, textAlign: 'start', fontWeight: 500 }}>
                      {t('dashboard.clinic.hidden', { count: formatNumber(hiddenByClinic) })}
                    </span>
                    <span>{t('dashboard.clinic.showAll')}</span>
                  </Button>
                </div>
              )}
              {/* Only when there is something to say: a low balance gets the
                  figure and the way to fix it; a healthy one is in «بیشتر». */}
              <MobileCreditCard balance={walletBalance} onTopUp={() => navigate('credits')} />
              {/* «نوبت جدید» — only where the doctor can book somewhere. On an
                  empty day the empty state carries the same action instead. */}
              {booking.canBook && futureList.length > 0 && (
                <div style={{ padding: '0.75rem 0.875rem 0' }}>
                  <Button
                    variant="outline"
                    data-testid="reception-book"
                    className="w-full gap-2 min-h-11"
                    onClick={booking.openBook}
                  >
                    <Plus size={16} aria-hidden /> {booking.s('dashboard.book.open')}
                  </Button>
                </div>
              )}
            </div>

            {!loading && nextApt && (
              <div style={{ paddingTop: '0.75rem' }}>
                <MobileNextPatientCard
                  apt={nextApt}
                  nowMin={nowMin}
                  nowMs={tick}
                  onStart={() => navigate('visit', { id: nextApt.patientId })}
                  onHistory={() => navigate('patient-detail', { id: nextApt.patientId })}
                  onEnd={() => askEnd(nextApt)}
                />
              </div>
            )}

            <div style={{ padding: '0 0.875rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>

              {/* Skeletons match the appointment-card geometry — never a page spinner. */}
              {loading && <ReceptionMobileListSkeleton count={3} />}

              {!loading && futureList.length === 0 && clinic.none && (
                <NoClinicGuide variant="mobile" pending={clinic.pending} onJoined={clinic.refresh} />
              )}

              {!loading && futureList.length === 0 && clinic.error && (
                <ErrorState error={clinic.error} title={t('errors.load.data')} onRetry={clinic.refresh} />
              )}

              {!loading && futureList.length === 0 && !clinic.none && !clinic.error && (
                <NoAppointments
                  icon={<CalendarDays size={48} color={C.textMuted} strokeWidth={1.5} aria-hidden />}
                  title={isToday ? t('dashboard.empty.noneToday') : t('dashboard.empty.none')}
                  description={isToday && counts.total > 0 ? t('dashboard.empty.allDone') : undefined}
                  doctorId={doctor?.id}
                  navigate={navigate}
                  onBook={booking.openBook}
                  canBook={booking.canBook}
                />
              )}

              {visibleAppointments.map(apt => (
                <MobileAppointmentCard
                  key={apt.id}
                  apt={apt}
                  testId={`reception-queue-row-${apt.id}`}
                  queuePos={model.queuePosOf.get(apt.id)}
                  nowMs={tick}
                  clinicName={clinicNameOf(apt)}
                  onOpen={() => apt.patientId && navigate('patient-detail', { ...apt, id: apt.patientId })}
                  onContinue={() => navigate('visit', { id: apt.patientId })}
                  onWithdraw={() => booking.askWithdraw(apt)}
                  onReschedule={booking.canReschedule(apt) ? () => booking.askReschedule(apt) : null}
                />
              ))}

              {hiddenCount > 0 && (
                <Button
                  variant="outline"
                  data-testid="reception-more"
                  className="w-full min-h-11"
                  onClick={() => setVisibleCount(c => c + PAGE_SIZE)}
                >
                  {t('dashboard.queue.showMore', { count: hiddenCount })}
                </Button>
              )}

              <div style={{ height: '1rem' }} />
            </div>
          </div>
        )}
      </div>

      {/* ── Sticky CTA — docked above the bottom nav ───────────────────────── */}
      {showCta && (
        <div style={{
          position: 'absolute', bottom: 0, insetInlineStart: 0, insetInlineEnd: 0,
          // §H.1 — `position` describes the LAYOUT viewport, which iOS does not
          // shrink for the keyboard. Without the inset this CTA sits under the
          // keys whenever the calendar sheet's year field is being typed into.
          padding: `0.75rem 1rem calc(0.75rem + ${keyboardInset}px)`,
          background: 'white',
          borderTop: `1px solid ${C.border}`,
          boxShadow: '0 -4px 20px rgba(0,0,0,0.08)',
          zIndex: 20,
        }}>
          <Button
            size="lg"
            data-testid="reception-cta-next"
            className="w-full gap-2.5 bg-gradient-brand font-extrabold"
            style={{ boxShadow: `0 4px 14px ${soft(C.navy, 0.35)}` }}
            onClick={() => navigate('visit', { id: nextApt.patientId })}
          >
            <PhoneCall size={18} aria-hidden />
            {t('dashboard.hero.callNext')}
            <span style={{ fontSize: '0.75rem', opacity: 0.75, fontWeight: 600 }}>· {nextApt.name?.split(' ')[0]}</span>
          </Button>
        </div>
      )}

      {/* Week browsing — the date label's sheet. It drives the same ?date=
          as the arrows, with an absolute ISO date like the desktop picker. */}
      <WeekPickerSheet
        open={weekOpen}
        onClose={() => setWeekOpen(false)}
        selISO={selISO}
        todayISO={todayISO}
        onPick={setSelISO}
        presence={presence}
        clinics={clinic.clinics}
      />

      <ConfirmDialog
        open={!!pendingEnd}
        onOpenChange={(o) => { if (!o) setPendingEnd(null); }}
        title={t('dashboard.endVisitConfirm.title')}
        message={t('dashboard.endVisitConfirm.message', {
          name: pendingEnd?.name || t('common.labels.patient'),
        })}
        confirmLabel={t('dashboard.endVisitConfirm.confirm')}
        cancelLabel={t('dashboard.endVisitConfirm.cancel')}
        destructive
        onConfirm={() => (pendingEnd ? endVisit(pendingEnd.id) : undefined)}
      />

      <BookAppointmentDialog
        open={booking.bookOpen}
        onClose={booking.closeBook}
        dayISO={selISO}
        clinicId={clinic.isAll ? null : clinic.activeId}
        bookingClinics={booking.bookingClinics}
        onBooked={booking.onBooked}
        dayAppointments={allAppointments}
        isMobile
      />

      <RescheduleAppointmentDialog
        open={!!booking.rescheduling}
        apt={booking.rescheduling}
        onClose={booking.closeReschedule}
        onRescheduled={booking.onRescheduled}
        isMobile
      />

      <ConfirmDialog
        open={!!booking.pendingWithdraw}
        onOpenChange={(o) => { if (!o) booking.cancelWithdraw(); }}
        title={booking.s('dashboard.withdraw.title')}
        message={booking.s('dashboard.withdraw.message', {
          name: booking.pendingWithdraw?.name || t('common.labels.patient'),
        })}
        confirmLabel={booking.s('dashboard.withdraw.confirm')}
        cancelLabel={booking.s('dashboard.withdraw.keep')}
        destructive
        onConfirm={booking.confirmWithdraw}
      />

    </div>
  );
};

export default ReceptionMobile;
